import { useId, useState } from 'react';

export type PasswordInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'>;

/**
 * Password field with a reveal toggle.
 *
 * Wraps a normal `<input>`, so every caller keeps its own `className`,
 * `autoComplete`, `aria-*` and validation wiring — this only owns the
 * masking state and the toggle button. `pr-11` is appended to the caller's
 * classes to reserve room for the button; Tailwind emits `pr-*` after `px-*`,
 * so it wins over the horizontal padding the call sites already set.
 *
 * The toggle is deliberately `tabIndex={-1}`: Tab should run from the
 * password field to the submit button, which is what password managers and
 * keyboard users expect. It stays reachable by click and by screen readers.
 */
export function PasswordInput({ className, ...props }: PasswordInputProps) {
  const [visible, setVisible] = useState(false);
  const generatedId = useId();
  const inputId = props.id ?? generatedId;

  return (
    <div className="relative">
      <input
        {...props}
        id={inputId}
        type={visible ? 'text' : 'password'}
        className={[className, 'pr-11'].filter(Boolean).join(' ')}
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
        aria-controls={inputId}
        // Opacity rather than a fixed colour: these fields sit on the light
        // theme, the dark theme, and JoinPage's always-dark panel, so a
        // hard-coded hover colour would be wrong on at least one of them.
        className="absolute inset-y-0 right-0 flex items-center px-3 text-warm-gray-400 opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus-visible:opacity-100"
      >
        <svg
          aria-hidden="true"
          className="h-5 w-5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {visible ? (
            <>
              <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
              <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
              <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
              <line x1="2" y1="2" x2="22" y2="22" />
            </>
          ) : (
            <>
              <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
              <circle cx="12" cy="12" r="3" />
            </>
          )}
        </svg>
      </button>
    </div>
  );
}
